import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { ID, Medium } from '@ianesthesie-kit/services';
import { TriageGroup } from '@/modules/appContext';
import { DataType } from '@/modules/terra';
import { GraphType, PredefinedGraph } from './graph.service';
import type { MedicalDocument } from '@/modules/surgicalEvent';

export type AppBarState = {
  /** True while the patient file's identity card is scrolled out of view, so the bar shows the name instead. */
  patientIdentityShown: boolean;
};

export type ImageViewerControls = {
  zoom: number;
  rotation: number;
  magnifierEnabled: boolean;
};

export type InvertedDocumentMedium = Medium & {
  medicalDocument: MedicalDocument;
};

export type ImageViewerState = {
  open: boolean;
  images?: InvertedDocumentMedium[];
  currentIndex?: number;
  controls: ImageViewerControls;
};

export type TableScrollMode = 'free' | 'sync';

export type MedicalHoViewMode = 'full' | 'minimal';
export type MedicalHoContentType = 'completeAndVerified' | 'nlp';

export type DashboardUiState = {
  lastPdvCpaDate?: string | null;
  lastPdvCpaTime?: string | null;
  headerMinWidths: {
    [group in TriageGroup]?: {
      [cellId: string]: number;
    };
  };
};

export type DashboardUiStatePayload = Partial<DashboardUiState>;

export type SectionName = string;
export type DoctorScriptExpandedState = Record<SectionName, boolean>;

export type NewPatientSnackbarPayload = { name: string; id: ID };

/** The dossier the user opened last, so the floating "back to patient" button can link to it. */
export type LastOpenedPatient = {
  id: ID;
  firstName: string;
  lastName: string;
  name: string;
};

export type CustomGraphFieldType = 'line' | 'bar' | 'scatter';

export type CustomGraphField = {
  name: string;
  path: string;
  type: CustomGraphFieldType;
};

export type CustomGraph = {
  name: string;
  dataType: DataType;
  fields: CustomGraphField[];
};

export type GraphState = {
  graphType?: GraphType;
  dateFrom?: string;
  dateTo?: string;
  customGraph?: CustomGraph;
};

export type GraphBuilderState = {
  dataType?: DataType;
  selectedFields: CustomGraphField[];
};

export type GraphBuilderFieldsPayload = {
  updateType: 'add' | 'remove' | 'replace';
  field: CustomGraphField;
};

export type WearableDataState = {
  dataType?: DataType;
  dateFrom?: string;
  dateTo?: string;
  latestOnly?: boolean;
};

interface UiState {
  uploadProgress?: number;
  appBar: AppBarState;
  imageViewer: ImageViewerState;
  dashboardUi: DashboardUiState;
  doctorScriptExpanded: DoctorScriptExpandedState;
  newPatientSnackbar?: NewPatientSnackbarPayload;
  lastOpenedPatient?: LastOpenedPatient;
  graph: GraphState;
  graphBuilder: GraphBuilderState;
  wearableDataState: WearableDataState;
  tabState: {
    visibility: DocumentVisibilityState;
    focus: boolean;
  };
}

export const initialImageViewerControls: ImageViewerControls = {
  zoom: 1,
  rotation: 0,
  magnifierEnabled: false // TODO
};

const imageViewerInitialState: ImageViewerState = {
  open: false,
  controls: initialImageViewerControls
};

const initialState: UiState = {
  appBar: {
    patientIdentityShown: false
  },
  imageViewer: imageViewerInitialState,
  dashboardUi: {
    headerMinWidths: {}
  },
  doctorScriptExpanded: {},
  graph: {},
  graphBuilder: { selectedFields: [] },
  wearableDataState: {},
  tabState: {
    visibility: 'visible',
    focus: true
  }
};

export type ImageViewerPayload = Partial<ImageViewerState>;

export type ZoomPayload =
  | 'reset'
  | {
      direction: 'in' | 'out';
      step?: number;
    };

export type RotationPayload = 'clockwise' | 'counterClockwise' | 'reset';

export const IMAGE_VIEWER_MIN_ZOOM = 0.4;
export const IMAGE_VIEWER_MAX_ZOOM = 1.8;
export const IMAGE_VIEWER_ZOOM_STEP = 0.2;

export const uiSlice = createSlice({
  name: 'appUi',
  initialState,
  reducers: {
    setUploadProgress: (
      state: Draft<UiState>,
      action: PayloadAction<number | undefined>
    ) => {
      state.uploadProgress = action.payload;
    },

    setAppBarPatientIdentityShown: (
      state: Draft<UiState>,
      action: PayloadAction<boolean>
    ) => {
      state.appBar.patientIdentityShown = action.payload;
    },

    patchImageViewerState: (
      state: Draft<UiState>,
      action: PayloadAction<ImageViewerPayload>
    ) => {
      state.imageViewer = {
        ...state.imageViewer,
        ...action.payload
      };
      if (state.imageViewer.images?.length && state.imageViewer.currentIndex) {
        if (state.imageViewer.currentIndex >= state.imageViewer.images.length) {
          state.imageViewer.currentIndex = state.imageViewer.images.length - 1;
        }
      }
    },
    zoomImageViewer: (
      state: Draft<UiState>,
      action: PayloadAction<ZoomPayload>
    ) => {
      if (action.payload === 'reset') {
        state.imageViewer.controls.zoom = 1;
      } else {
        const { direction, step = IMAGE_VIEWER_ZOOM_STEP } = action.payload;
        const currentZoom = state.imageViewer.controls.zoom;
        let newZoom =
          direction === 'in' ? currentZoom + step : currentZoom - step;
        if (newZoom < IMAGE_VIEWER_MIN_ZOOM) {
          newZoom = IMAGE_VIEWER_MIN_ZOOM;
        } else if (newZoom > IMAGE_VIEWER_MAX_ZOOM) {
          newZoom = IMAGE_VIEWER_MAX_ZOOM;
        }
        state.imageViewer.controls.zoom = newZoom;
      }
    },
    rotateImageViewer: (
      state: Draft<UiState>,
      action: PayloadAction<RotationPayload>
    ) => {
      const rotation = state.imageViewer.controls.rotation;
      if (action.payload === 'reset') {
        state.imageViewer.controls.rotation = 0;
      } else {
        state.imageViewer.controls.rotation =
          action.payload === 'clockwise' ? rotation + 90 : rotation - 90;
      }
    },
    goToNextImageViewerImage: (state: Draft<UiState>) => {
      const { images = [], currentIndex = 0 } = state.imageViewer;
      if (currentIndex < images.length - 1) {
        state.imageViewer.currentIndex = currentIndex + 1;
      }
    },
    goToPrevImageViewerImage: (state: Draft<UiState>) => {
      const { currentIndex = 0 } = state.imageViewer;
      if (currentIndex > 0) {
        state.imageViewer.currentIndex = currentIndex - 1;
      }
    },

    patchDashboardUiState: (
      state: Draft<UiState>,
      action: PayloadAction<DashboardUiStatePayload>
    ) => {
      if ('headerMinWidths' in action.payload) {
        throw new Error('Use specific action for updating cell widths');
      }
      state.dashboardUi = {
        ...state.dashboardUi,
        ...action.payload
      };
    },
    updateHeaderMinWidths: (
      state: Draft<UiState>,
      action: PayloadAction<{
        group: TriageGroup;
        headerMinWidths: Record<string, number>;
      }>
    ) => {
      const group = action.payload.group;
      state.dashboardUi.headerMinWidths[group] = {
        ...state.dashboardUi.headerMinWidths[group],
        ...action.payload.headerMinWidths
      };
    },
    resetHeaderMinWidths: (
      state: Draft<UiState>,
      action: PayloadAction<TriageGroup>
    ) => {
      const group = action.payload;
      state.dashboardUi.headerMinWidths[group] = {};
    },
    toggleDoctorScript: (
      state: Draft<UiState>,
      action: PayloadAction<SectionName>
    ) => {
      state.doctorScriptExpanded[action.payload] =
        !state.doctorScriptExpanded[action.payload];
    },
    setNewPatientSnackbar: (
      state: Draft<UiState>,
      action: PayloadAction<NewPatientSnackbarPayload>
    ) => {
      state.newPatientSnackbar = action.payload;
    },
    clearNewPatientSnackbar: (state: Draft<UiState>) => {
      state.newPatientSnackbar = undefined;
    },
    setLastOpenedPatient: (
      state: Draft<UiState>,
      action: PayloadAction<LastOpenedPatient>
    ) => {
      state.lastOpenedPatient = action.payload;
    },

    setGraphType: (
      state: Draft<UiState>,
      action: PayloadAction<PredefinedGraph>
    ) => {
      state.graph.graphType = action.payload;
      state.graph.customGraph = undefined;
    },
    setGraphDate: (
      state: Draft<UiState>,
      action: PayloadAction<Partial<Pick<GraphState, 'dateFrom' | 'dateTo'>>>
    ) => {
      state.graph = {
        ...state.graph,
        dateFrom: action.payload?.dateFrom,
        dateTo: action.payload?.dateTo
      };
    },
    setCustomGraph: (
      state: Draft<UiState>,
      action: PayloadAction<CustomGraph>
    ) => {
      state.graph.graphType = GraphType.CUSTOM;
      state.graph.customGraph = action.payload;
    },

    setGraphBuilderState: (
      state: Draft<UiState>,
      action: PayloadAction<GraphBuilderState>
    ) => {
      state.graphBuilder = {
        ...action.payload
      };
    },
    updateGraphBuilderFields: (
      state: Draft<UiState>,
      action: PayloadAction<GraphBuilderFieldsPayload>
    ) => {
      const { field, updateType } = action.payload;
      if (!field || !updateType) {
        return;
      }
      const selected = state.graphBuilder.selectedFields;
      if (updateType === 'add') {
        if (!selected.find((f) => f.path === field.path)) {
          state.graphBuilder.selectedFields = [field, ...selected];
        }
      } else if (updateType === 'remove') {
        state.graphBuilder.selectedFields = selected.filter(
          (f) => f.path !== field.path
        );
      } else if (updateType === 'replace') {
        state.graphBuilder.selectedFields = selected.map((f) =>
          f.path === field.path ? field : f
        );
      }
    },

    setWearableDataType: (
      state: Draft<UiState>,
      action: PayloadAction<DataType | undefined>
    ) => {
      const st = state.wearableDataState;
      if (action.payload && !st.dataType && !st.dateFrom && !st.dateTo) {
        // if is initial set
        st.latestOnly = true;
      }
      st.dataType = action.payload;
    },
    setWearableDataPeriod: (
      state: Draft<UiState>,
      action: PayloadAction<
        Partial<Pick<WearableDataState, 'dateFrom' | 'dateTo' | 'latestOnly'>>
      >
    ) => {
      state.wearableDataState = {
        ...state.wearableDataState,
        ...action.payload
      };
      const s = state.wearableDataState;
      if (!s.dateFrom && !s.dateTo) {
        s.latestOnly = true;
      }
    },
    clearWearableDataState: (state: Draft<UiState>) => {
      state.wearableDataState = {};
    },
    updateTabState: (
      state,
      action: PayloadAction<Partial<UiState['tabState']>>
    ) => {
      state.tabState = {
        ...state.tabState,
        ...action.payload
      };
    }
  }
});

export const {
  setUploadProgress,
  setAppBarPatientIdentityShown,
  patchImageViewerState,
  zoomImageViewer,
  rotateImageViewer,
  goToPrevImageViewerImage,
  goToNextImageViewerImage,
  patchDashboardUiState,
  updateHeaderMinWidths,
  resetHeaderMinWidths,
  toggleDoctorScript,
  setNewPatientSnackbar,
  clearNewPatientSnackbar,
  setLastOpenedPatient,
  setGraphType,
  setGraphDate,
  setCustomGraph,
  setGraphBuilderState,
  updateGraphBuilderFields,
  setWearableDataType,
  setWearableDataPeriod,
  clearWearableDataState,
  updateTabState
} = uiSlice.actions;
export const { reducer: appUiReducer } = uiSlice;
